import _ from 'lodash';
import moment from 'moment-timezone';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAsyncEffect } from 'rooks';
import { sqWorkbenchStore, sqWorkbookStore } from '@/core/core.stores';
import { useFluxPath } from '@/core/hooks/useFluxPath.hook';
import { SaveAndContinueFormBuilder } from '@/formbuilder/SaveAndContinueFormBuilder.page';
import { ImpactReportSlider } from '@/impactReports/ImpactReportSlider.atom';
import { KpiLabelWithDescription } from '@/impactReports/KpiLabelWithDescription.atom';
import { SelectedProjectDisplayAndLink } from '@/impactReports/SelectedProjectDisplayAndLink.atom';
import { SelectedWorksheetDisplayAndLink } from '@/impactReports/SelectedWorksheetDisplayAndLink.atom';
import { ImpactReportAIOverlay } from '@/impactReports/ValueCaptureAIOverlay.atom';
import {
  DAILY_FREQUENCY,
  IMPACT_OWNER_FIRSTNAME,
  IMPACT_OWNER_LASTNAME,
  IMPACT_REPORT_LABEL_ID,
  MONTHLY_FREQUENCY,
  QUARTERLY_FREQUENCY,
  TRACK_IMPACT,
  WEEKLY_FREQUENCY,
  YEARLY_FREQUENCY,
  availableStages,
  instructions,
  stageIcons,
  toImpactReportSelectedItemType,
} from '@/impactReports/impactReport.types';
import {
  addOrUpdateImpactReport,
  giveEveryoneGroupReadPermissions,
  removeEveryoneGroupReadPermissions,
  stageDisplayStringByIndex,
  verifyEveryoneGroupReadPermissions,
} from '@/impactReports/impactReport.utilities';
import {
  getCustomCategoryTitle,
  getKPIDisplayName,
  getKPIExpectedAnnualColumnName,
  getKPIItemColumnName,
  getKPIUnit,
} from '@/impactReports/impactReportDisplay.utilities';
import {
  useImpactReportActions,
  useImpactReportExpectedAnnualKpiColumns,
  useImpactReportExpectedAnnualKpiVisibility,
  useImpactReportKpiColumns,
  useImpactReportShowAssetPath,
  useImpactReportShowLocation,
  useImpactReportShowSite,
  useImpactReportTableId,
} from '@/impactReports/impactReports.store';
import { useImpactReportApiActions } from '@/impactReports/useImpactReportApiActions.hook';
import { ReportContentWorksheetAndUrl } from '@/reportEditor/components/reportContentModal/shared/ReportContentWorksheetAndUrl.molecule';
import { hasAnalyticsAdminCapability } from '@/services/authorization.service';
import { genAIEnabled } from '@/services/systemConfiguration.utilities';
import { doTrack } from '@/track/track.service';
import { randomUUID, validateGuid } from '@/utilities/guid.utilities';
import { logError } from '@/utilities/logger';
import { errorToast } from '@/utilities/toast.utilities';
import { SelectWorksheet } from '@/worksheets/SelectWorksheet.molecule';
import { sqContextApi } from '@seeqdev/api/endpoint/ContextApi';
import { sqWorkbooksApi } from '@seeqdev/api/endpoint/WorkbooksApi';
import { SeeqNames } from '@seeqdev/constants';
import { Alert, Button, Icon } from '@seeqdev/qomponents';
import type { Dispatch, SetStateAction } from 'react';
import type { FormElement } from '@/formbuilder/formBuilder.constants';

import type {
  ImpactReportCategory,
  ImpactReportSelectedItemType,
  ImpactType,
  Location,
  Stages,
} from '@/impactReports/impactReport.types';
import type { SelectedSearchItem } from '@/search/search.types';
import type { ContextLabelWithIdInputV1 } from '@seeqdev/api/model/ContextLabelWithIdInputV1';
import type { IdentityPreviewV1 } from '@seeqdev/api/model/IdentityPreviewV1';
import type { ScalarPropertyV1 } from '@seeqdev/api/model/ScalarPropertyV1';

interface ImpactReportModalProps {
  existingReport?: string;
  stageIndex?: number;
  workbookId?: string;
  worksheetId?: string;
  selectedItemType: ImpactReportSelectedItemType;
  setWorkbookId: (id: string) => void;
  setWorksheetId: (id: string) => void;
  setSelectedItemType: Dispatch<SetStateAction<ImpactReportSelectedItemType>>;
  closeFn: (reloadTable?: boolean) => void;
  setStageIndex: (idx: number) => void;
  report?: Record<string, any>;
  categories?: ImpactReportCategory[];
}

interface ImpactReportHeaderProps {
  workbookId: string;
  worksheetId: string;
  selectedItemType: ImpactReportSelectedItemType;
  stageIndex: number;
  setStageIndex: (idx: number) => void;
  setWorkbookId: (id: string) => void;
  setWorksheetId: (id: string) => void;
  setSelectedItemType: Dispatch<SetStateAction<ImpactReportSelectedItemType>>;
}

const validSuffixes = ['/h', '/d', '/min', '/mo', '/ms', '/ns', '/s', '/wk', '/y', '']; // '' for just the unit itself, e.g. '$'

function isValidUnit(unit: string): boolean {
  return validSuffixes.some((suffix) => unit.endsWith(suffix));
}

function hasItemSelectionValue(value: unknown): boolean {
  if (validateGuid(value)) return true;
  if (value && typeof value === 'object' && validateGuid((value as { id?: unknown }).id)) return true;
  return false;
}

const getValidUnitForDisplay = (unit: string) => (unit.indexOf('·') > 0 ? unit.substring(0, unit.indexOf('·')) : unit);

export const ImpactReportHeader = ({
  workbookId,
  worksheetId,
  selectedItemType,
  stageIndex = 0,
  setStageIndex,
  setWorksheetId,
  setWorkbookId,
  setSelectedItemType,
}: ImpactReportHeaderProps) => {
  const { t } = useTranslation();
  const [currentStage, setCurrentStage] = useState<Stages | null>(null);
  const [currentStageDisplay, setCurrentStageDisplay] = useState('');
  const dataLabProjectSelected = selectedItemType === SeeqNames.Types.Project;

  useEffect(() => {
    setCurrentStage(availableStages[stageIndex]);
    setCurrentStageDisplay(t(`IMPACT_REPORT.${availableStages[stageIndex]?.toUpperCase()}`));
  }, [stageIndex, t]);

  return (
    <div className="flexRowContainer valueCapture width-maximum">
      <div className="tw:mb-sq-5">
        <h4>{t('IMPACT_REPORT.CAPTURE_IMPACT')}</h4>
      </div>
      <div className="flexColumnContainer tw:flex-wrap">
        {workbookId && dataLabProjectSelected ? (
          <div className="flexRowContainer flexFill tw:mt-0 tw:items-start">
            <div>{t('IMPACT_REPORT.SELECTED_PROJECT')}</div>
            <div>
              <SelectedProjectDisplayAndLink projectId={workbookId} />
              <Icon
                icon="fa-close"
                onClick={() => {
                  setWorkbookId('');
                  setWorksheetId('');
                  setSelectedItemType('');
                  setStageIndex(0);
                }}
                extraClassNames="ml10 mt3 cursorPointer"
              />
            </div>
          </div>
        ) : workbookId && worksheetId ? (
          <div className="flexRowContainer flexFill tw:mt-0 tw:items-start">
            <div>{t('IMPACT_REPORT.SELECTED_WORKSHEET')}</div>
            <div>
              <SelectedWorksheetDisplayAndLink workbookId={workbookId} worksheetId={worksheetId} />
              <Icon
                icon="fa-close"
                onClick={() => {
                  setWorkbookId('');
                  setWorksheetId('');
                  setSelectedItemType('');
                  setStageIndex(0);
                }}
                extraClassNames="ml10 mt3 cursorPointer"
              />
            </div>
          </div>
        ) : (
          <div className="flexFill" />
        )}
        {currentStage && (
          <div className="flexRowContainer tw:items-end">
            <h6 className="mtn8">
              {t('IMPACT_REPORT.CURRENT_STAGE')} {currentStageDisplay}
            </h6>
            <ImpactReportSlider setStageIdx={setStageIndex} stageIndex={stageIndex} />
          </div>
        )}
      </div>
    </div>
  );
};

export const ImpactReportForm = ({
  stageIndex = 0,
  workbookId,
  worksheetId,
  selectedItemType,
  setWorkbookId,
  setWorksheetId,
  setSelectedItemType,
  setStageIndex,
  closeFn,
  report,
  categories,
}: ImpactReportModalProps) => {
  const isTopic = useFluxPath(sqWorkbookStore, () => sqWorkbookStore.isTopic);
  const currentUser = useFluxPath(sqWorkbenchStore, () => sqWorkbenchStore.currentUser);
  const canReviewReports = hasAnalyticsAdminCapability();
  const kpiColumns = useImpactReportKpiColumns();
  const expectedAnnualKpiColumns = useImpactReportExpectedAnnualKpiColumns();
  const expectedAnnualKpiVisibility = useImpactReportExpectedAnnualKpiVisibility();
  const showSite = useImpactReportShowSite();
  const showLocation = useImpactReportShowLocation();
  const showAssetPath = useImpactReportShowAssetPath();
  const { setUpdateNominations } = useImpactReportActions();
  const { t } = useTranslation();
  const [impactType, setImpactType] = useState<ImpactType>('oneTime');
  const [rowDatumId, setRowDatumId] = useState<string | undefined>(
    report && report[SeeqNames.MaterializedTables.DatumIdColumn],
  );
  // we don't need a store, but we need to store the values in local state to ensure we can persist them across stages
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState(genAIEnabled() ? '' : instructions);
  const [owner, setOwner] = useState({
    id: sqWorkbenchStore.currentUser.id,
    name: sqWorkbenchStore.currentUser.name,
  });
  const [customCategories, setCustomCategories] = useState<{ [key: string]: any }>({});
  const [showItemSelection, setShowItemSelection] = useState<{ [columnName: string]: boolean }>({});

  const [asset, setAsset] = useState('');
  const [impactStartDate, setImpactStartDate] = useState<moment.Moment>(moment().subtract(1, 'year'));
  const [impactEndDate, setImpactEndDate] = useState<moment.Moment | undefined>(undefined);
  const [impactFrequency, setImpactFrequency] = useState('');
  const [location, setLocation] = useState<Location | undefined>(undefined);
  const [customKPIValues, setCustomKPIValues] = useState<{ [key: string]: any }>({});
  const [customKPIExpectedAnnualValues, setCustomKPIExpectedAnnualValues] = useState<Record<string, string>>({});
  const [reviewComment, setReviewComment] = useState('');
  const [reviewApprovalConfirmed, setReviewApprovalConfirmed] = useState(false);
  const [formDirty, setFormDirty] = useState<string[]>([]);
  const [site, setSite] = useState(sqWorkbenchStore.currentUser.site);
  const [forceUpdateText, setForceUpdateText] = useState(description);
  const dataLabProjectSelected = selectedItemType === SeeqNames.Types.Project;
  // this is a temporary solution - for now we do not support generating AI description for Vantage
  const [isVantage, setIsVantage] = useState(false);
  const [hasReadPermissions, setHasReadPermissions] = useState(false);

  const readPermissionRef = React.useRef(false);

  const impactFrequencyOptions = [
    { value: DAILY_FREQUENCY, label: t('IMPACT_REPORT.FREQUENCIES.DAILY') },
    { value: WEEKLY_FREQUENCY, label: t('IMPACT_REPORT.FREQUENCIES.WEEKLY') },
    { value: MONTHLY_FREQUENCY, label: t('IMPACT_REPORT.FREQUENCIES.MONTHLY') },
    { value: QUARTERLY_FREQUENCY, label: t('IMPACT_REPORT.FREQUENCIES.QUARTERLY') },
    { value: YEARLY_FREQUENCY, label: t('IMPACT_REPORT.FREQUENCIES.YEARLY') },
  ];

  useAsyncEffect(async () => {
    if (!workbookId) return;
    const everyoneCanRead = await verifyEveryoneGroupReadPermissions(workbookId);
    setHasReadPermissions(everyoneCanRead);
    readPermissionRef.current = everyoneCanRead;
  }, [workbookId]);

  useEffect(() => {
    setIsVantage(selectedItemType === SeeqNames.Types.Vantage);
  }, [selectedItemType]);

  useAsyncEffect(async () => {
    if (!workbookId || selectedItemType) return;
    const workbookResponse = await sqWorkbooksApi.getWorkbook({ id: workbookId });
    setSelectedItemType(toImpactReportSelectedItemType(workbookResponse.data?.type));
  }, [workbookId, selectedItemType]);
  // end temporary code

  const [showAIOverlay, setShowAIOverlay] = useState(description === '');
  const tableId = useImpactReportTableId();
  const { loadImpactReportTableId } = useImpactReportApiActions();
  const supportsAI = Boolean(workbookId && worksheetId && !isVantage && !dataLabProjectSelected);

  useAsyncEffect(async () => {
    await loadImpactReportTableId();
  }, []);

  useEffect(() => {
    setShowAIOverlay(supportsAI && description === '');
    if (!supportsAI && workbookId && !description) {
      setForceUpdateText(instructions);
    }
  }, [supportsAI, workbookId, description]);

  useEffect(() => {
    if (!report || _.isEmpty(report)) return;
    setAsset(report[SeeqNames.ImpactReport.ImpactAsset]);
    setTitle(report[SeeqNames.ImpactReport.ImpactName]);
    setDescription(report[SeeqNames.ImpactReport.ImpactDescription]);
    setImpactEndDate(
      report[SeeqNames.ImpactReport.ImpactEndDate] && moment(report[SeeqNames.ImpactReport.ImpactEndDate]),
    );
    setImpactStartDate(
      report[SeeqNames.ImpactReport.ImpactStartDate] && moment(report[SeeqNames.ImpactReport.ImpactStartDate]),
    );
    setImpactFrequency(report[SeeqNames.ImpactReport.ImpactFrequency]);
    setWorksheetId(report[SeeqNames.ImpactReport.AssignedToWorksheet] ?? '');
    setWorkbookId(report[SeeqNames.ImpactReport.AssignedToWorkbook] ?? '');
    setSelectedItemType(toImpactReportSelectedItemType(report[SeeqNames.ImpactReport.ImpactBasedOn]));
    setImpactType(report[SeeqNames.ImpactReport.ImpactSavingsType]);
    setSite(report[SeeqNames.ImpactReport.ImpactSite]);
    if (report[SeeqNames.ImpactReport.Location]) {
      setLocation(JSON.parse(report[SeeqNames.ImpactReport.Location]));
    }
    setOwner({
      id: report[SeeqNames.ImpactReport.ImpactOwner],
      name: `${report[IMPACT_OWNER_FIRSTNAME]} ${report[IMPACT_OWNER_LASTNAME]}`,
    } as IdentityPreviewV1);

    const customCats: Record<string, string>[] | undefined =
      categories?.map((category) => {
        const cat = category.category;
        return { [cat.id!]: report[cat.id!] };
      }) || [];
    setCustomCategories(customCats);

    // custom KPIs can be using calculated items, so we need to ensure we set the state values to the saved item and
    // not the calculated impact as that will lose the item reference
    const kpiValues: Record<string, string | number> = kpiColumns?.reduce(
      (acc, column) => {
        const itemColumName = getKPIItemColumnName(column.columnName);
        if (!report[itemColumName]) {
          report[itemColumName] = report[column.columnName];
        }
        const possibleItemId = report[itemColumName];
        acc[column.columnName] = validateGuid(possibleItemId) ? { id: possibleItemId } : possibleItemId;
        return acc;
      },
      {} as Record<string, number | string>,
    );
    setCustomKPIValues(kpiValues);
    setShowItemSelection(
      kpiColumns?.reduce(
        (acc, column) => {
          const value = _.has(kpiValues, column.columnName)
            ? kpiValues[column.columnName]
            : (report[`${column.columnName}_item`] ?? report[column.columnName]);
          acc[column.columnName] = hasItemSelectionValue(value);
          return acc;
        },
        {} as Record<string, boolean>,
      ),
    );

    const expectedAnnualValues = expectedAnnualKpiColumns.reduce(
      (acc, column) => {
        const value = report[column.columnName];
        if (value !== undefined && value !== null) {
          acc[column.columnName] = String(value);
        }
        return acc;
      },
      {} as Record<string, string>,
    );
    setCustomKPIExpectedAnnualValues(expectedAnnualValues);
    setReviewComment(report[SeeqNames.ImpactReport.ReviewComment] ?? '');
  }, [categories, expectedAnnualKpiColumns, kpiColumns, report, setSelectedItemType, setWorkbookId, setWorksheetId]);

  const currentStage = availableStages[stageIndex];
  const originalReviewedBy = report?.[SeeqNames.ImpactReport.ReviewedBy];
  const originalReviewComment = report?.[SeeqNames.ImpactReport.ReviewComment] ?? '';
  const originalReviewDate = report?.[SeeqNames.ImpactReport.ReviewDate];
  const hasPersistedReviewMetadata = Boolean(originalReviewComment || originalReviewDate || originalReviewedBy);
  const persistedStatus = report?.[SeeqNames.ImpactReport.ImpactStatus] as Stages | undefined;
  const anyFieldChanged = formDirty.length > 0 || persistedStatus !== currentStage;

  // Simplified review-status rules:
  // - Non-admin editing a reviewed report: any change reverts it back to Quantify (review fields preserved).
  // - Admin editing a reviewed report: stage stays Review; the approval toggle gates the save whenever
  //   anything changed, and the admin becomes the recorded reviewer with a fresh review date.
  // - Otherwise: status follows the slider (existing wizard flow).
  const willRevertReviewToQuantify = !canReviewReports && persistedStatus === 'review' && anyFieldChanged;
  const effectiveStage: Stages = willRevertReviewToQuantify ? 'quantify' : currentStage;
  const requiresReviewApprovalConfirmation = canReviewReports && anyFieldChanged && currentStage === 'review';
  const resolvedReviewedBy = !originalReviewedBy || currentUser.id;

  useEffect(() => {
    if (currentStage === 'review' || !reviewApprovalConfirmed) return;
    setReviewApprovalConfirmed(false);
  }, [currentStage, reviewApprovalConfirmed]);

  const sectionHeader = (stageIndex: number) => (
    <>
      <div
        className={`flexColumnContainer mbn16 formSectionHeader tw:items-center ${stageIndex !== 0 ? 'tw:mt-5' : ''}`}>
        <Icon icon={stageIcons[stageIndex]} extraClassNames="mr10 ml5" />
        <h4 className="flexFill">{stageDisplayStringByIndex(stageIndex)}</h4>
      </div>
      <hr />
    </>
  );

  if (!tableId) return null;

  const expectedAnnualColumnNames = new Set(expectedAnnualKpiColumns.map((column) => column.columnName));

  const kpiFields = kpiColumns
    ? kpiColumns.map((kpi) => {
        const value = _.has(customKPIValues, kpi.columnName)
          ? customKPIValues[kpi.columnName]
          : report && report[kpi.columnName];
        const expectedAnnualColumnName = getKPIExpectedAnnualColumnName(kpi.columnName);
        const includeExpectedAnnual =
          expectedAnnualKpiVisibility[kpi.columnName] && expectedAnnualColumnNames.has(expectedAnnualColumnName);
        const expectedAnnualValue = _.has(customKPIExpectedAnnualValues, expectedAnnualColumnName)
          ? customKPIExpectedAnnualValues[expectedAnnualColumnName]
          : report && report[expectedAnnualColumnName];
        const unit = getKPIUnit(kpi.columnName);
        const valueRowComponents: FormElement[] = [
          {
            component: 'ToggleInputSelectItemSearchFormComponent' as const,
            label: t('IMPACT_REPORT.PROVIDED_VALUE'),
            name: kpi.columnName,
            onChange: (item: string | SelectedSearchItem[] | number | null) => {
              if (typeof item === 'string' || typeof item === 'number') {
                setCustomKPIValues({ ...customKPIValues, [kpi.columnName]: item });
              } else if (typeof item === 'object') {
                if (item?.length === 1) {
                  const selectedItem = item[0] as any;
                  // verify the units - if they are invalid we have to make sure we let the user know or they can end
                  // up with a very broken table :(
                  const itemUnit = selectedItem['valueUnitOfMeasure'];
                  const kpiUnit = getKPIUnit(kpi.columnName);
                  if (
                    (kpiUnit.indexOf('·') > -1 && itemUnit === kpiUnit.replace('·', '/')) ||
                    itemUnit === kpiUnit.substring(0, kpiUnit.indexOf('·')) ||
                    (itemUnit?.startsWith(kpiUnit) && isValidUnit(itemUnit))
                  ) {
                    setCustomKPIValues({ ...customKPIValues, [kpi.columnName]: selectedItem });
                    return;
                  }
                  errorToast({
                    messageKey: 'IMPACT_REPORT.INVALID_UNIT',
                    messageParams: { unit: getKPIUnit(kpi.columnName) },
                  });
                }
              }
            },
            unit: _.find(impactFrequencyOptions, { value: impactFrequency })?.label,
            required: false,
            showItemSelection: showItemSelection[kpi.columnName],
            setShowItemSelection: (columnName: string, shouldShowSelection: boolean) => {
              setShowItemSelection((prev) => ({ ...prev, [columnName]: shouldShowSelection }));
            },
            size: 'sm' as const,
            testId: kpi.columnName,
            value: validateGuid(value?.id) ? [value] : value,
            isMultiple: false,
            notSelectedMessage: t('IMPACT_REPORT.FIND_CUSTOM_CALC'),
            title: t('IMPACT_REPORT.FIND_CUSTOM_CALC'),
            overrideWorkbookId: workbookId,
            scopeIds: [workbookId],
            searchTypes: ['Signal'],
            showToggle: impactType !== 'oneTime',
            iconPartialTooltipKey: 'IMPACT_REPORT.CUSTOM_CALC_TOOLTIP',
            customCalcInstructions: t('IMPACT_REPORT.CUSTOM_CALC_INSTRUCTIONS', {
              unit: getValidUnitForDisplay(getKPIUnit(kpi.columnName)),
            }),
          } as FormElement,
        ];

        if (includeExpectedAnnual) {
          valueRowComponents.push({
            component: 'TextFieldFormComponent',
            label: t('IMPACT_REPORT.EXPECTED_ANNUAL_SAVINGS'),
            name: expectedAnnualColumnName,
            value: expectedAnnualValue ?? '',
            onChange: (newValue) => {
              setCustomKPIExpectedAnnualValues({
                ...customKPIExpectedAnnualValues,
                [expectedAnnualColumnName]: newValue as string,
              });
            },
            size: 'sm',
            type: 'number',
            min: 0,
            testId: expectedAnnualColumnName,
            required: false,
            wrapperClassNames: 'width-30percent ml10',
          } as FormElement);
        }

        const components: FormElement[] = [
          {
            component: 'DisplayOnlyFormElementWrapper',
            name: `${kpi.columnName}-header`,
            children: (
              <div className="tw:mb-sq-5">
                <KpiLabelWithDescription
                  label={`${getKPIDisplayName(kpi.columnName)} (${unit})`}
                  description={kpi.description}
                />
              </div>
            ),
          },
          {
            component: 'FormRow',
            name: `${kpi.columnName}-values`,
            components: valueRowComponents,
          } as FormElement,
        ];

        return {
          component: 'FormGroup',
          name: `${kpi.columnName}-group`,
          formGroupExtraClassNames: 'flexColumnContainer p10 simpleBorder grayBorder roundedBorder mb10',
          components,
        } as FormElement;
      })
    : [];

  const categoryFields = categories
    ? categories.map((category) => {
        const cat = category.category;
        const fieldName = getCustomCategoryTitle(category);
        const options = category.options;
        const categoryId = cat.id!;
        return {
          label: fieldName,
          component: 'SelectFormComponent',
          name: categoryId,
          value: customCategories[categoryId] ? customCategories[categoryId] : report && report[categoryId],
          required: false,
          options: options as any,
          onChange: (value) => {
            setCustomCategories({ ...customCategories, [categoryId]: value });
          },
        } as FormElement;
      })
    : [];

  const updateCKContent = (content: string) => {
    setDescription(description + content);
    setForceUpdateText(description + content);
  };

  const formDefinition: FormElement[] = [
    {
      component: 'FormGroup',
      name: 'identify',
      includeIf: stageIndex >= 0,
      components: [
        {
          component: 'DisplayOnlyFormElementWrapper',
          name: 'identifyHeader',
          children: sectionHeader(0),
        },
        {
          component: 'TextFieldFormComponent',
          name: 'title',
          label: 'Title',
          value: title,
          required: true,
          onChange: (title) => {
            setTitle(title as string);
          },
          size: 'sm',
          testId: 'formTitle',
        },
        {
          component: 'DisplayOnlyFormElementWrapper',
          name: 'CKEditorLabel',
          children: [
            <div className="flexColumnContainer tw:mb-sq-5 tw:items-center" key="CKLabel">
              {t('IMPACT_REPORT.DESCRIPTION')}
              {genAIEnabled() && !showAIOverlay && worksheetId && workbookId && !isVantage && (
                <Icon
                  onClick={() => setShowAIOverlay(true)}
                  icon="fa-sparkles"
                  iconPrefix="fa-solid"
                  extraClassNames="ml5 cursorPointer"
                />
              )}
            </div>,
          ],
        },
        {
          component: 'CKEditorFormComponent',
          name: 'description',
          label: '',
          extraClassNames: 'width-maximum',
          value: description,
          required: true,
          onChange: (description) => {
            setDescription(description as string);
          },
          testId: 'description',
          forceUpdateText,
          overlay:
            genAIEnabled() && showAIOverlay && supportsAI ? (
              <ImpactReportAIOverlay
                onClose={() => setShowAIOverlay(false)}
                updateCKContent={updateCKContent}
                worksheetId={worksheetId}
                workbookId={workbookId}
              />
            ) : undefined,
          placeholder: '',
        },
        {
          component: 'SelectIdentityFormComponent',
          name: 'owner',
          idForLabel: 'useCaseOwner',
          label: 'Use case owner',
          extraClassNames: 'width-maximum mb25',
          value: owner,
          required: true,
          startEditable: true,
          onChange: (selectedIdentity) => {
            setOwner(_.pick(selectedIdentity, ['id', 'name']) as IdentityPreviewV1);
          },
          testId: 'owner',
        },
        {
          component: 'CheckboxFormComponent',
          id: 'grantReadPermissions',
          name: 'grantReadPermissions',
          label: '',
          onChange: async () => {
            setHasReadPermissions(!hasReadPermissions);
          },
          skipToFormState: true,
          checkboxLabel: t('IMPACT_REPORT.READ_PERMISSIONS_REQUIRED'),
          value: hasReadPermissions,
        },
      ],
    },

    {
      component: 'FormGroup',
      name: 'categorize',
      includeIf: stageIndex >= 1,
      components: [
        {
          component: 'DisplayOnlyFormElementWrapper',
          name: 'categoryHeader',
          children: sectionHeader(1),
        },
        {
          component: 'LabelFormComponent',
          name: 'locationLabel',
          value: 'Location',
          includeIf: showLocation,
        },
        {
          component: 'LocationLookupFormComponent',
          name: 'locationLookup',
          extraClassNames: 'mb8',
          label: 'Location',
          value: location,
          onChange: (location: Location) => {
            setLocation(location);
          },
          testId: 'location',
          required: false,
          includeIf: showLocation,
        },
        {
          component: 'TextFieldFormComponent',
          name: 'site',
          label: 'Site',
          value: site,
          onChange: (site) => {
            setSite(site as string);
          },
          size: 'sm',
          testId: 'site',
          includeIf: showSite,
        },
        ...categoryFields,
        {
          component: 'TextFieldFormComponent',
          name: 'asset',
          label: 'Asset type',
          value: asset,
          onChange: (asset) => {
            setAsset(asset as string);
          },
          size: 'sm',
          testId: 'assetType',
          includeIf: showAssetPath,
        },
      ],
    },
    {
      component: 'FormGroup',
      name: 'quantify',
      includeIf: stageIndex >= 2,

      components: [
        {
          component: 'DisplayOnlyFormElementWrapper',
          name: 'quantifyHeader',
          children: sectionHeader(2),
        },
        {
          component: 'LabelFormComponent',
          name: 'impactType',
          value: 'Impact type',
          extraClassNames: '',
        },
        {
          component: 'FormRow',
          name: 'justARow',
          components: [
            {
              component: 'CheckboxFormComponent',
              name: 'oneTime',
              type: 'radio',
              value: impactType === 'oneTime',
              onChange: () => {
                setImpactType('oneTime');
                setImpactFrequency('');
                setShowItemSelection(
                  kpiColumns?.reduce(
                    (acc, column) => {
                      acc[column.columnName] = false;
                      return acc;
                    },
                    {} as Record<string, boolean>,
                  ),
                );
              },
              checkboxLabel: 'One time impact',
              id: 'oneTime',
              testId: 'oneTime',
              skipToFormState: true,
              classes: 'flexAlignCenter mr20',
            },
            {
              component: 'CheckboxFormComponent',
              name: 'Continuous',
              type: 'radio',
              value: impactType === 'continuous',
              onChange: () => {
                setImpactType('continuous');
              },
              checkboxLabel: 'continuous impact',
              id: 'continuous',
              testId: 'continuous',
              classes: 'flexAlignCenter mr20',
            },
          ],
        },
      ],
    },
    {
      component: 'FormGroup',
      name: 'startEndDateRow',
      includeIf: stageIndex >= 2,
      components: [
        {
          includeIf: impactType !== 'customCalc',
          component: 'FormRow',
          name: 'startEndRow',
          extraClassNames: 'flexAlignCenter',
          components: [
            {
              component: 'DatePickerFormComponent',
              name: 'impactStartDate',
              label: t('IMPACT_REPORT.START_DATE'),
              value: impactStartDate,
              required: true,
              onChange: (date) => {
                setImpactStartDate(date);
              },
              testId: 'impactStartDate',
              wrapperClassNames: 'mr5',
            },
            {
              component: 'DatePickerFormComponent',
              name: 'impactEndDate',
              includeIf: impactType === 'continuous',
              label: t('IMPACT_REPORT.END_DATE'),
              value: impactEndDate,
              onChange: (date) => {
                setImpactEndDate(date);
              },
              testId: 'impactEndDate',
              wrapperClassNames: 'ml15',
            },
            {
              component: 'FormGroup',
              name: 'continuousImpact',
              includeIf: impactType === 'continuous',
              formGroupExtraClassNames: 'ml20 max-width-150',
              components: [
                {
                  label: t('IMPACT_REPORT.FREQUENCY'),
                  component: 'SelectFormComponent',
                  name: 'impactFrequency',
                  value: impactFrequency,
                  options: impactFrequencyOptions,
                  required: impactType === 'continuous',
                  onChange: (frequency) => {
                    setImpactFrequency(frequency as string);
                  },
                },
              ],
            },
          ],
        },
        {
          component: 'FormRow',
          name: 'monetizeFormRow',
          components: [
            {
              component: 'FormGroup',
              name: 'kpis',
              components: [...kpiFields],
            },
          ],
        },
      ],
    },

    {
      component: 'DisplayOnlyFormElementWrapper',
      name: 'reviewedReportEditWarning',
      includeIf: !canReviewReports && persistedStatus === 'review' && anyFieldChanged,
      children: (
        <Alert
          dismissible={false}
          variant="warning"
          testId="reviewedReportEditWarning"
          extraClassNames="tw:mb-2 tw:mt-2">
          <span>
            <Icon icon="fa-warning m3" type="warning" tooltipPlacement="right" />{' '}
            {t('IMPACT_REPORT.EDIT_REVERTS_REVIEW')}
          </span>
        </Alert>
      ),
    },
    {
      component: 'FormGroup',
      name: 'review',
      includeIf: currentStage === 'review',
      components: [
        {
          component: 'DisplayOnlyFormElementWrapper',
          name: 'reviewHeader',
          children: sectionHeader(3),
        },
        {
          component: 'TextFieldFormComponent',
          name: 'reviewComment',
          label: t('IMPACT_REPORT.REVIEW_COMMENT'),
          value: reviewComment,
          size: 'sm',
          as: 'textarea',
          rows: 4,
          onChange: (comment) => {
            setReviewComment(comment as string);
          },
          testId: 'reviewComment',
        },
        {
          component: 'ToggleFormComponent',
          name: 'reviewApprovalConfirmed',
          value: reviewApprovalConfirmed,
          onChange: () => {
            setReviewApprovalConfirmed((prev) => !prev);
          },
          iconSize: '2x',
          title: t('IMPACT_REPORT.REVIEW_APPROVAL_TOGGLE_TITLE'),
          label: t('IMPACT_REPORT.REVIEW_APPROVAL_TOGGLE_DESCRIPTION'),
          labelPosition: 'right',
          testId: 'reviewApprovalConfirmation',
        },
      ],
    },
  ];

  const onFormSubmitCallback = async (values: any) => {
    const properties: Array<ScalarPropertyV1> = [];
    const addIfNotEmpty = (name: string, value: string | boolean | null | undefined) => {
      if (value) {
        properties.push({ name, value });
      }
    };
    addIfNotEmpty(SeeqNames.ImpactReport.ImpactName, title);
    addIfNotEmpty(SeeqNames.ImpactReport.ImpactDescription, description);
    addIfNotEmpty(SeeqNames.ImpactReport.ImpactOwner, owner?.id);

    addIfNotEmpty(SeeqNames.ImpactReport.ImpactSite, site);
    addIfNotEmpty(SeeqNames.ImpactReport.ImpactAsset, asset);

    addIfNotEmpty(SeeqNames.ImpactReport.ImpactSavingsType, impactType);
    addIfNotEmpty(SeeqNames.ImpactReport.ImpactStartDate, impactStartDate && impactStartDate?.toISOString());
    addIfNotEmpty(SeeqNames.ImpactReport.ImpactEndDate, impactEndDate && impactEndDate?.toISOString());

    addIfNotEmpty(SeeqNames.ImpactReport.ImpactFrequency, impactFrequency);

    addIfNotEmpty(SeeqNames.ImpactReport.AssignedToWorksheet, worksheetId);
    addIfNotEmpty(SeeqNames.ImpactReport.AssignedToWorkbook, workbookId);
    addIfNotEmpty(SeeqNames.ImpactReport.ImpactStatus, effectiveStage);
    if (effectiveStage === 'review') {
      const resolvedReviewDate = moment().toISOString();
      properties.push({ name: SeeqNames.ImpactReport.ReviewComment, value: reviewComment ?? '' });
      addIfNotEmpty(SeeqNames.ImpactReport.ReviewDate, resolvedReviewDate);
      addIfNotEmpty(SeeqNames.ImpactReport.ReviewedBy, resolvedReviewedBy);
    } else if (hasPersistedReviewMetadata) {
      properties.push({ name: SeeqNames.ImpactReport.ReviewComment, value: '' });
      properties.push({ name: SeeqNames.ImpactReport.ReviewDate, value: '' });
      properties.push({ name: SeeqNames.ImpactReport.ReviewedBy, value: '' });
      setReviewComment('');
    }

    addIfNotEmpty(SeeqNames.ImpactReport.Location, JSON.stringify(location));
    const isContinuous = impactType === 'continuous';
    const hasManualSelection = (kpiColumns ?? []).some((column) => {
      const columnName = column.columnName;
      const value = customKPIValues[columnName];
      const isItemSelection = Boolean(showItemSelection[columnName]);
      return !isItemSelection && value !== undefined && value !== null && value !== '';
    });
    const unit = _.find(impactFrequencyOptions, { value: impactFrequency })?.label;
    if (isContinuous && hasManualSelection && !unit) {
      errorToast({
        messageKey: 'IMPACT_REPORT.FREQUENCY_REQUIRED',
        messageParams: { unit },
      });
      return Promise.reject();
    }
    // ensure custom kpi values are set
    kpiColumns?.forEach((column) => {
      const columnName = column.columnName;
      let value = customKPIValues[columnName];
      if (!value) return;
      value = typeof value === 'string' || typeof value === 'number' ? value : value.id;
      addIfNotEmpty(columnName, value);
    });
    expectedAnnualKpiColumns.forEach((column) => {
      const value = customKPIExpectedAnnualValues[column.columnName];
      if (!value) return;
      addIfNotEmpty(column.columnName, value);
    });

    // if we are updating an existing report, we need to use the existing datum id
    let useThisDatumId = rowDatumId;
    if (useThisDatumId === undefined) {
      doTrack(TRACK_IMPACT, 'Created new Impact Report', { isTopic, impactType });
      useThisDatumId = randomUUID();
    } else {
      doTrack(TRACK_IMPACT, 'Updated existing Impact Report', { isTopic, impactType });
    }

    properties.push({ name: SeeqNames.MaterializedTables.ItemIdColumn, value: tableId });
    properties.push({ name: SeeqNames.MaterializedTables.DatumIdColumn, value: useThisDatumId });

    /**
     * TODO: CRAB-46894:
     * As part of CRAB-46894 please remove the next line of code and ensure that the datum_id column rules are
     * removed so that a simple "datum id" property is used as the value of the datum id.
     *
     *
     * How we got here:
     * Due to the lack of a table type for Materialized Tables a migration that changes the datum_id to be expecting
     * a property named "Existing Row Identifier" is applied to the ImpactReport Table. To ensure updates of impact
     * reports work as expected we need to also include a property called "Existing Row Identifier" with our Events.
     * The value is simple the datum id of the row we are updating (as it's still being returned as the datum id).
     */
    properties.push({ name: SeeqNames.MaterializedTables.UniqueRowId, value: useThisDatumId });

    // remove all previously assigned context labels to avoid problems
    if (categories) {
      const contextLabels: ContextLabelWithIdInputV1[] = _.chain(categories)
        .map((category: ImpactReportCategory) => {
          const labelId = values[category.category.id!];
          return labelId
            ? {
                contextLabelId: report && report[`${category.category.id}_${IMPACT_REPORT_LABEL_ID}`],
                datumId: useThisDatumId,
                labelId,
              }
            : null;
        })
        .compact()
        .value();
      try {
        await sqContextApi.batchCreateContextLabels(contextLabels, { itemId: tableId });
      } catch (error: any) {
        logError(error);
      }
    }

    await addOrUpdateImpactReport(tableId, properties);
    setRowDatumId(useThisDatumId); // ensure this gets set so the "continue" button works

    if (readPermissionRef.current !== hasReadPermissions) {
      if (hasReadPermissions) {
        await giveEveryoneGroupReadPermissions(workbookId!);
      } else {
        await removeEveryoneGroupReadPermissions(workbookId!);
      }
      readPermissionRef.current = hasReadPermissions;
    }
  };

  return (
    <div className="flexRowContainer flexFill valueCapture tw:mb-sq-10 tw:items-center">
      {!workbookId && !worksheetId && (
        <div className="flexRowContainer flexFill width-maximum tw:mt-sq-15 tw:items-center">
          <div className="flexColumnContainer mbn16 formSectionHeader width-maximum tw:mb-sq-10 tw:items-center">
            <h4 className="tw:ml-sq-10">{t('IMPACT_REPORT.SELECT_WORKSHEET_AND_WORKBOOK')}</h4>
          </div>
          <hr />
          <ReportContentWorksheetAndUrl
            selectOnlyWorkbooks={true}
            allowedTypes={[
              SeeqNames.Types.Topic,
              SeeqNames.Types.Analysis,
              SeeqNames.Types.Vantage,
              SeeqNames.Types.Folder,
              SeeqNames.Types.Project,
            ]}
            extraClassNames="width-maximum"
            setSelectedItem={(item) => {
              setSelectedItemType(toImpactReportSelectedItemType(item?.type));
              setWorkbookId(item?.id ?? '');
            }}
          />
          <Button label={t('IMPACT_REPORT.CONTINUE')} disabled={!workbookId} extraClassNames="mt10" />
        </div>
      )}
      {workbookId && !worksheetId && !dataLabProjectSelected && (
        <div className="flexRowContainer flexFill tw:mt-sq-15 tw:items-center">
          <div className="flexColumnContainer mbn16 formSectionHeader width-maximum tw:mb-sq-10 tw:items-center">
            <h4 className="tw:ml-sq-10">{t('IMPACT_REPORT.SELECT_WORKSHEET')}</h4>
          </div>
          <hr />
          <SelectWorksheet
            workbookId={workbookId}
            worksheetIds={[]}
            setWorksheetIds={(worksheetIds) => {
              if (worksheetIds?.length > 0) setWorksheetId(worksheetIds[0]);
            }}
            workstepIds={[]}
            setWorkstepIds={() => {}}
            setTotalWorksheets={() => {}}
            isLoading={false}
          />
          <Button label={t('IMPACT_REPORT.CONTINUE')} disabled={!worksheetId} extraClassNames="mt10" />
        </div>
      )}

      {workbookId && (worksheetId || dataLabProjectSelected) && (
        <SaveAndContinueFormBuilder
          submitBtnLabel={t('IMPACT_REPORT.SAVE_AND_CLOSE')}
          submitBtnId="saveAndClose"
          submitBtnDisabled={requiresReviewApprovalConfirmation && !reviewApprovalConfirmed}
          extraClassNames="flexFill tw:overflow-auto height-maximum"
          continueButtonLabel={t('IMPACT_REPORT.SAVE_AND_CONTINUE')}
          continueButtonPosition="center"
          continueButtonExtraClassNames="mr10"
          continueButtonEnabled={
            stageIndex < availableStages.length - 1 &&
            (availableStages[stageIndex + 1] !== 'review' || canReviewReports)
          }
          continueButtonOnSubmit={async (values) => {
            await onFormSubmitCallback(values);
            if (willRevertReviewToQuantify) {
              setStageIndex(availableStages.indexOf('quantify'));
            } else if (
              stageIndex < availableStages.length - 1 &&
              (availableStages[stageIndex + 1] !== 'review' || canReviewReports)
            ) {
              setStageIndex(stageIndex + 1);
            }
            doTrack(TRACK_IMPACT, 'Save and Continue Button', {
              stageIndex,
              anyFieldChanged,
              willRevertReviewToQuantify,
            });
          }}
          continueButtonVariant="theme"
          onDirtyChange={setFormDirty}
          formDefinition={formDefinition}
          submitFn={async (values) => {
            await onFormSubmitCallback(values);
            setRowDatumId(undefined);
            closeFn(true);
            setUpdateNominations(true);
            doTrack(TRACK_IMPACT, 'Save and Close Button', {
              stageIndex,
              anyFieldChanged,
              willRevertReviewToQuantify,
            });
          }}
          closeFn={closeFn}
        />
      )}
    </div>
  );
};
